/*
 * @format
 */

import _intersection from 'lodash/intersection'
import _isEmpty from 'lodash/isEmpty'

import { getPrefix } from '../../../../core/utils/locale'
import { fromObjectToParams } from '../../../../utils/from_object_to_params'

import {
  DEFAULT_ORDER,
  HOTEL_WITHOUT_PRICE,
  PAGINATION_CONFIG,
  PRICE_ORDER_OPTIONS,
  VALID_ORDER_VALUES,
} from './pagination_constants'
import PAGINATION_STATE from './pagination_state'

//
// MANAGER CLASS
//
// It is a wrapper around PAGINATION_STATE
//
export class PaginationManager {
  constructor(blockName, htmlTagDataset, initialIDs, date = null) {
    // initialize the pagination state
    this.state = PAGINATION_STATE

    // sets max resources per page from config data
    this.maxPerPage = PAGINATION_CONFIG[blockName]?.maxResourcesPerPage
    this.setResourceIDs(initialIDs)
    this._setInitialResourceIDs(initialIDs)

    // prepares endpoint data, reads the lang prefix, mandatory params and specific param
    this.prefix = this._processPrefix()
    this.controllerAction = PAGINATION_CONFIG[blockName]?.action
    this.mandatoryParams = this._processMandatoryConstantParams(htmlTagDataset)
    this.resourceParam = PAGINATION_CONFIG[blockName]?.resourceParam

    this.state.idsToAppend = []
    this.state.idsWithoutPrice = []

    if (date != null) {
      this.state.date = date
    }
  }

  getResourceIDs() {
    return this.state.resourceIDs.slice()
  }

  setResourceIDs(ids) {
    let clonedIDs = ids.slice()
    clonedIDs = this._castToIntArray(clonedIDs)
    this.state.resourceIDs = this._splitInPages(clonedIDs)
  }

  getRawResourceIDs() {
    let hotelIDs = this.state.resourceIDs
      .slice()
      .flat()
      .filter(x => !this.state.idsToAppend.includes(x))
    const idsToAppend = this.state.idsToAppend.filter(x => this.state.resourceIDs.slice().flat().includes(x))
    if (idsToAppend.length) hotelIDs = hotelIDs.concat(idsToAppend)
    return hotelIDs
  }

  setIdsToAppend(idsToAppend) {
    this.state.idsToAppend = idsToAppend || []
  }
  setIdsWithoutPrice(idsWithoutPrice) {
    this.state.idsWithoutPrice = idsWithoutPrice || []
  }

  getIdsToAppend() {
    return this.state.idsToAppend
  }

  setMaxPerPage(callerBlockName) {
    this.maxPerPage = PAGINATION_CONFIG[callerBlockName]?.maxResourcesPerPage
  }

  getCurrentPage() {
    return this.state.currentPage
  }

  setCurrentPage(page) {
    this.state.currentPage = page
  }

  getOrder() {
    return this.state.order
  }

  setOrder(order) {
    if (order === this.state.order) return

    if (VALID_ORDER_VALUES.includes(order)) {
      const newOrder = order
      const oldOrder = this.state.order
      this.state.order = newOrder
      this._isPriceOrderCriteria(newOrder) && this._reverseOrder()
    } else {
      console.warn(`Invalid order type: ${order}. Valid types: ${VALID_ORDER_VALUES}`)
      console.warn('Check correct attribute in your options HTML')
    }
  }

  setDate(date) {
    this.state.date = date
  }

  getDate() {
    return this.state.date
  }

  getControllerAction() {
    return this.controllerAction
  }

  getUrlWithMandatoryParams() {
    return `${this.prefix}${this.controllerAction}?${this.mandatoryParams}&page=${this.getCurrentPage()}&${this.getOrder()}=true&date=${this.getDate()}`
  }

  getEndpoint({ ...customParams } = {}) {
    let url = `${this.getUrlWithMandatoryParams()}&${this.resourceParam}=[${this.getRawResourceIDs()}]`
    url += _isEmpty(customParams) ? '' : `&${fromObjectToParams(customParams)}`
    url += _isEmpty(this.state.idsWithoutPrice) ? '' : `&${HOTEL_WITHOUT_PRICE}=${this.state.idsWithoutPrice}`
    return url
  }

  updateState({ ids, order, page = 1 }) {
    ids && this.setResourceIDs(ids)
    order && this.setOrder(order)
    this.setCurrentPage(page)
  }

  // Handle with care. Not only will it reset pagination to different IDs
  // but will also change the initialIds variable and that can mess up
  // with ordering options.
  // Useful when:
  //    - Obtaining CRS hotels ordered by price.
  //
  forceStateResetTo({ ids, order = DEFAULT_ORDER, page = 1 }) {
    this.updateState({ ids, order, page })
    this._setInitialResourceIDs(ids)
  }

  getCurrentPageResourceIDs() {
    return this.state.currentPageResourceIDs
  }

  _setInitialResourceIDs(ids) {
    let initialIDs = ids.slice()
    initialIDs = this._castToIntArray(initialIDs)
    this.state.initialResourceIDs = initialIDs
  }

  _processPrefix() {
    let prefix = getPrefix() || '/'
    prefix = prefix === '/' ? prefix : `${prefix}/`
    return prefix
  }

  _processMandatoryConstantParams(htmlTagDataset) {
    const params = {
      market_id: htmlTagDataset.marketId,
      deviceType: htmlTagDataset.deviceType,
      site: 'iberostar',
      microsite: IB.is_in_microsite ? htmlTagDataset.inMicrosite : '',
      user_level: htmlTagDataset.level,
    }

    return fromObjectToParams(params)
  }

  _splitInPages(ids) {
    const resourceIds = this._castToIntArray(ids).slice()

    const paginatedInArray = []
    while (resourceIds.length) {
      paginatedInArray.push(resourceIds.splice(0, this.maxPerPage))
    }

    return paginatedInArray
  }

  _castToIntArray(ids) {
    ids = typeof ids === 'string' ? JSON.parse(ids) : ids
    ids = ids.map(id => +id)
    return ids
  }

  _isPriceOrderCriteria(order) {
    return PRICE_ORDER_OPTIONS.includes(order)
  }

  _reverseOrder() {
    const currentIDs = this.getRawResourceIDs().slice()
    const initialIDs = this.state.initialResourceIDs.slice()

    let orderedIDs = []
    if (this.state.order === PRICE_ORDER_OPTIONS[0]) {
      orderedIDs = _intersection(initialIDs, currentIDs)
      orderedIDs = orderedIDs.length ? orderedIDs : currentIDs
    } else {
      orderedIDs = _intersection(initialIDs.reverse(), currentIDs)
    }

    this.setResourceIDs(orderedIDs)
  }
}
